<script setup lang="ts">
/**
 * Saisie d'un code PIN : 4 pastilles masquées et pavé numérique. Émet `complete` dès le
 * 4ᵉ chiffre. `error` secoue légèrement les pastilles (désactivé si mouvement réduit).
 */
import { computed, ref, watch } from 'vue'

import { fr } from '@/copy/fr'

import Keypad from './Keypad.vue'

const props = withDefaults(defineProps<{ length?: number; error?: string; disabled?: boolean }>(), {
  length: 4,
  error: undefined,
  disabled: false,
})

const emit = defineEmits<{ complete: [pin: string] }>()

const digits = ref('')

const dots = computed(() =>
  Array.from({ length: props.length }, (_, index) => index < digits.value.length),
)

function append(value: string) {
  if (props.disabled || digits.value.length >= props.length) return
  digits.value += value
  if (digits.value.length === props.length) emit('complete', digits.value)
}

watch(
  () => props.error,
  (error) => {
    if (error) digits.value = ''
  },
)

defineExpose({ reset: () => (digits.value = '') })
</script>

<template>
  <div class="flex flex-col items-center gap-8">
    <div
      :class="['flex gap-4', error ? 'animate-shake motion-reduce:animate-none' : '']"
      role="status"
      :aria-label="fr.ui.pinProgress(digits.length, length)"
    >
      <span
        v-for="(filled, index) in dots"
        :key="index"
        :class="[
          'size-3.5 rounded-full transition-colors duration-80',
          error ? 'bg-danger' : filled ? 'bg-ink' : 'bg-surface-strong',
        ]"
      />
    </div>
    <p v-if="error" class="-mt-4 text-label text-danger" role="alert">{{ error }}</p>
    <Keypad
      class="w-full"
      :extra-key="null"
      :disabled="disabled"
      @digit="append"
      @backspace="digits = digits.slice(0, -1)"
      @clear="digits = ''"
    />
  </div>
</template>
